import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';

import { AlertController } from '@ionic/angular';

import { AES256 } from 'aes-everywhere';

import { Observable, catchError, map, throwError } from 'rxjs';

import {
  API_URL,
  ENCRYPT,
  PASSPHRASE_AUTH,
} from './../../../environments/environment';

import { AuthenticatorService } from './authenticator.service';
import { ToastStatus, UtilsService } from '../utils.service';

export interface ApiResponse {
  data?: any;
  status?: string;
  message?: string;
  responseCode?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private key!: string;

  constructor(
    private _http: HttpClient,
    private alertController: AlertController,
    private auth: AuthenticatorService,
    private utilsService: UtilsService
  ) {}

  public get(
    endpoint: string,
    data: any = {},
    api_url: string = API_URL
  ): Observable<any> {
    return new Observable((observer) => {
      (async () => {
        try {
          let key: any;

          if (ENCRYPT) {
            if (this.key === undefined || this.key === null) {
              this.key = await this.getToken();
            }

            key = AES256.decrypt(this.key, PASSPHRASE_AUTH);
          }

          const headers: HttpHeaders = this.headers();

          if (
            (typeof data !== 'object' || data !== '' || data !== null) &&
            ENCRYPT &&
            Object.keys(data).length > 0
          ) {
            data = {
              data: AES256.encrypt(JSON.stringify(data), key),
            };
          }

          this._http
            .get(api_url + endpoint + '/', {
              params: data,
              headers: headers,
            })
            .pipe(
              map((res: ApiResponse) => this.handleResponse(res)),
              catchError((error) => this.handleErrors(error))
            )
            .subscribe({
              next: (response: ApiResponse) => {
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
                observer.complete();
              },
            });
        } catch (error) {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        }
      })();
    });
  }

  public post(
    endpoint: string,
    data: any = {},
    api_url: string = API_URL
  ): Observable<any> {
    return new Observable((observer) => {
      (async () => {
        try {
          let key: any;

          if (ENCRYPT) {
            if (this.key === undefined || this.key === null) {
              this.key = await this.getToken();
            }

            key = AES256.decrypt(this.key, PASSPHRASE_AUTH);
          }

          const headers: HttpHeaders = this.headers();

          if (
            (typeof data !== 'object' || data !== '' || data !== null) &&
            ENCRYPT
          ) {
            data = {
              data: AES256.encrypt(JSON.stringify(data), key),
            };
          }

          this._http
            .post(api_url + endpoint, data, {
              headers: headers,
            })
            .pipe(
              map((res: ApiResponse) => this.handleResponse(res)),
              catchError((error) => this.handleErrors(error))
            )
            .subscribe({
              next: (response: ApiResponse) => {
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
                observer.complete();
              },
            });
        } catch (error) {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        }
      })();
    });
  }

  public put(
    endpoint: string,
    data: any = {},
    api_url: string = API_URL
  ): Observable<any> {
    return new Observable((observer) => {
      (async () => {
        try {
          let key: any;

          if (ENCRYPT) {
            if (this.key === undefined || this.key === null) {
              this.key = await this.getToken();
            }

            key = AES256.decrypt(this.key, PASSPHRASE_AUTH);
          }

          const headers: HttpHeaders = this.headers();

          if (
            (typeof data !== 'object' || data !== '' || data !== null) &&
            ENCRYPT
          ) {
            data = {
              data: AES256.encrypt(JSON.stringify(data), key),
            };
          }

          this._http
            .put(api_url + endpoint, data, {
              headers: headers,
            })
            .pipe(
              map((res: ApiResponse) => this.handleResponse(res)),
              catchError((error) => this.handleErrors(error))
            )
            .subscribe({
              next: (response: ApiResponse) => {
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
                observer.complete();
              },
            });
        } catch (error) {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        }
      })();
    });
  }

  public delete(
    endpoint: string,
    data: any = {},
    api_url: string = API_URL
  ): Observable<any> {
    return new Observable((observer) => {
      (async () => {
        try {
          let key: any;

          if (ENCRYPT) {
            if (this.key === undefined || this.key === null) {
              this.key = await this.getToken();
            }

            key = AES256.decrypt(this.key, PASSPHRASE_AUTH);
          }

          const headers: HttpHeaders = this.headers();

          if (
            (typeof data !== 'object' || data !== '' || data !== null) &&
            ENCRYPT &&
            Object.keys(data).length > 0
          ) {
            data = {
              data: AES256.encrypt(JSON.stringify(data), key),
            };
          }

          this._http
            .delete(api_url + endpoint + '/', {
              params: data,
              headers: headers,
            })
            .pipe(
              map((res: ApiResponse) => this.handleResponse(res)),
              catchError((error) => this.handleErrors(error))
            )
            .subscribe({
              next: (response: ApiResponse) => {
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
                observer.complete();
              },
            });
        } catch (error) {
          this.handleError(error);
          observer.error(error);
          observer.complete();
        }
      })();
    });
  }

  private handleResponse(res: ApiResponse): any {
    if (res.responseCode === 401) {
      this.logout();
    }

    if (res.data !== undefined && ENCRYPT && this.key !== undefined) {
      let key = AES256.decrypt(this.key, PASSPHRASE_AUTH);
      res.data = JSON.parse(AES256.decrypt(res.data, key));
    }

    return res;
  }

  private handleErrors(error: HttpErrorResponse): Observable<never> {
    if (error.status === 401) {
      this.logout();
    }

    if (error.error.data !== undefined && ENCRYPT && this.key !== undefined) {
      let key = AES256.decrypt(this.key, PASSPHRASE_AUTH);
      error.error.data = JSON.parse(AES256.decrypt(error.error.data, key));
    }

    return throwError(() => error.error);
  }

  private handleError(error: any): void {
    this.utilsService.toast(
      'Ocorreu um erro, tente novamente!',
      ToastStatus.Error
    );
  }

  private headers(): HttpHeaders {
    const user = this.utilsService.getStorage('usuario');

    if (user !== null) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + user.jwt,
        Encrypt: ENCRYPT ? 'true' : 'false',
      });
    } else {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        Encrypt: ENCRYPT ? 'true' : 'false',
      });
    }
  }

  public async getToken() {
    const promise: any = new Promise(async (resolve: any) => {
      try {
        this._http
          .get(API_URL + this.auth.endpoint() + '/', {
            headers: new HttpHeaders({
              Encrypt: ENCRYPT ? 'true' : 'false',
            }),
          })
          .subscribe(
            async (res: ApiResponse) => {
              if (res.responseCode != null && res.responseCode === 401) {
                await this.logout();
              }

              resolve(res.data);
            },
            async (res: HttpErrorResponse) => {
              if (res.status === 401) {
                await this.logout();
              }
            }
          );
      } catch (error) {
        console.log(error);
        this.utilsService.toast(
          'Ocorreu um erro, tente novamente!',
          ToastStatus.Error
        );
      }
    });

    return promise;
  }

  private async logout(): Promise<void> {
    const alert: HTMLIonAlertElement = await this.alertController.create({
      header: 'Sessão expirada',
      message: 'Faça login novamente para renovar sua sessão.',
      buttons: [
        {
          text: 'Ok',
          role: 'cancel',
        },
      ],
    });

    await alert.present();
  }
}
